import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { isOrderNumberForOrderId, type OrderNumber, OrderNumberSchema } from "./order-number.js";
import {
  type PaidSaleReturnPaymentMethod,
  PaidSaleReturnPaymentMethodSchema,
} from "./paid-sale-return.js";

export const REFUND_BUSINESS_METRIC_DETAIL_CONTRACT_VERSION =
  "REFUND_BUSINESS_METRIC_DETAIL_V1" as const;
export const REFUND_BUSINESS_METRIC_DETAIL_CAPACITY = 5_000;

export const RefundBusinessNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^TK-[0-9]{8}-[0-9A-F]{12}$",
});
export type RefundBusinessNumber = Static<typeof RefundBusinessNumberSchema>;

const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;
const STABLE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u;

function isCanonicalDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    /^\d{4}-\d{2}-\d{2}$/u.test(value) &&
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function fortyEightBitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function deriveRefundBusinessNumber(
  businessDate: string,
  refundId: string,
): RefundBusinessNumber {
  if (!isCanonicalDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (
    !STABLE_ID_PATTERN.test(refundId) ||
    [...refundId].filter((character) => /[A-Za-z0-9]/u.test(character)).length < 12
  ) {
    throw new TypeError("refundId must contain at least 12 safe alphanumeric characters");
  }
  return `TK-${businessDate.replaceAll("-", "")}-${fortyEightBitHex(
    `TK\0${businessDate}\0${refundId}`,
  )}`;
}

export function isRefundBusinessNumber(value: unknown): value is RefundBusinessNumber {
  if (!Value.Check(RefundBusinessNumberSchema, value)) return false;
  const match = /^TK-(\d{4})(\d{2})(\d{2})-[0-9A-F]{12}$/u.exec(value);
  return Boolean(match && isCanonicalDate(`${match[1]}-${match[2]}-${match[3]}`));
}

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const MoneyFenSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const SharedItemProperties = {
  refundNumber: RefundBusinessNumberSchema,
  orderNumber: OrderNumberSchema,
  orderBusinessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
  tableNumber: Type.String({ minLength: 1, maxLength: 40 }),
  paymentMethod: PaidSaleReturnPaymentMethodSchema,
  originalPaymentAmountFen: MoneyFenSchema,
  amountFen: MoneyFenSchema,
  reason: Type.String({ minLength: 2, maxLength: 200 }),
  createdAt: Type.String({ minLength: 1, maxLength: 64 }),
} as const;

export const RefundBusinessMetricDetailSourceItemSchema = Type.Object(
  {
    refundId: StableIdSchema,
    orderId: StableIdSchema,
    paymentId: StableIdSchema,
    actorId: StableIdSchema,
    authorizerId: StableIdSchema,
    ...SharedItemProperties,
    evidence: Type.Literal("MANUAL_FALLBACK"),
  },
  { additionalProperties: false },
);
export type RefundBusinessMetricDetailSourceItem = Static<
  typeof RefundBusinessMetricDetailSourceItemSchema
>;

export const RefundBusinessMetricDetailItemSchema = Type.Object(
  {
    ...SharedItemProperties,
    operatorDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    authorizerDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    dataSource: Type.Literal("STORE_RECORD"),
  },
  { additionalProperties: false },
);
export type RefundBusinessMetricDetailItem = Static<typeof RefundBusinessMetricDetailItemSchema>;

const EnvelopeProperties = {
  contractVersion: Type.Literal(REFUND_BUSINESS_METRIC_DETAIL_CONTRACT_VERSION),
  tenantId: Type.String({ minLength: 1, maxLength: 128 }),
  storeId: Type.String({ minLength: 1, maxLength: 128 }),
  businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
  timeZone: Type.String({ minLength: 1, maxLength: 80 }),
  businessDayCutover: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
  rangeStartedAt: Type.String({ minLength: 1, maxLength: 64 }),
  rangeEndedAt: Type.String({ minLength: 1, maxLength: 64 }),
  generatedAt: Type.String({ minLength: 1, maxLength: 64 }),
  coverage: Type.Literal("COMPLETE_REFUND_LEDGER"),
  valueFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
  factCount: Type.Integer({ minimum: 0, maximum: REFUND_BUSINESS_METRIC_DETAIL_CAPACITY }),
} as const;

export const RefundBusinessMetricDetailSnapshotSchema = Type.Object(
  {
    ...EnvelopeProperties,
    items: Type.Array(RefundBusinessMetricDetailSourceItemSchema, {
      maxItems: REFUND_BUSINESS_METRIC_DETAIL_CAPACITY,
    }),
  },
  { additionalProperties: false },
);
export type RefundBusinessMetricDetailSnapshot = Static<
  typeof RefundBusinessMetricDetailSnapshotSchema
>;

export const RefundBusinessMetricDetailReadModelSchema = Type.Object(
  {
    ...EnvelopeProperties,
    items: Type.Array(RefundBusinessMetricDetailItemSchema, {
      maxItems: REFUND_BUSINESS_METRIC_DETAIL_CAPACITY,
    }),
  },
  { additionalProperties: false },
);
export type RefundBusinessMetricDetailReadModel = Static<
  typeof RefundBusinessMetricDetailReadModelSchema
>;

type DetailEnvelope = Pick<
  RefundBusinessMetricDetailReadModel,
  | "tenantId"
  | "storeId"
  | "businessDate"
  | "timeZone"
  | "businessDayCutover"
  | "rangeStartedAt"
  | "rangeEndedAt"
  | "generatedAt"
  | "valueFen"
  | "factCount"
>;

type SharedItem = {
  refundNumber: RefundBusinessNumber;
  orderNumber: OrderNumber;
  orderBusinessDate: string;
  tableNumber: string;
  paymentMethod: PaidSaleReturnPaymentMethod;
  originalPaymentAmountFen: number;
  amountFen: number;
  reason: string;
  createdAt: string;
};

function envelopeIsConsistent(value: DetailEnvelope): boolean {
  if (
    !isCanonicalDate(value.businessDate) ||
    !isCanonicalTimestamp(value.rangeStartedAt) ||
    !isCanonicalTimestamp(value.rangeEndedAt) ||
    !isCanonicalTimestamp(value.generatedAt) ||
    Date.parse(value.rangeEndedAt) <= Date.parse(value.rangeStartedAt) ||
    (value.factCount === 0) !== (value.valueFen === 0)
  ) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("zh-CN", { timeZone: value.timeZone }).format(new Date(0));
  } catch {
    return false;
  }
  return true;
}

function itemIsConsistent(item: SharedItem, envelope: DetailEnvelope): boolean {
  const createdAt = Date.parse(item.createdAt);
  return (
    isRefundBusinessNumber(item.refundNumber) &&
    item.refundNumber.slice(3, 11) === envelope.businessDate.replaceAll("-", "") &&
    isCanonicalDate(item.orderBusinessDate) &&
    isCanonicalTimestamp(item.createdAt) &&
    createdAt >= Date.parse(envelope.rangeStartedAt) &&
    createdAt < Date.parse(envelope.rangeEndedAt) &&
    createdAt <= Date.parse(envelope.generatedAt) &&
    item.amountFen <= item.originalPaymentAmountFen &&
    item.tableNumber === item.tableNumber.trim() &&
    item.reason === item.reason.trim()
  );
}

function itemsAreConsistent(items: readonly SharedItem[], envelope: DetailEnvelope): boolean {
  if (items.length !== envelope.factCount) return false;
  const refundNumbers = new Set<string>();
  let valueFen = 0;
  let previousCreatedAt = Number.POSITIVE_INFINITY;
  let previousRefundNumber = "";
  for (const item of items) {
    const createdAt = Date.parse(item.createdAt);
    if (
      refundNumbers.has(item.refundNumber) ||
      !itemIsConsistent(item, envelope) ||
      createdAt > previousCreatedAt ||
      (createdAt === previousCreatedAt &&
        previousRefundNumber !== "" &&
        item.refundNumber <= previousRefundNumber)
    ) {
      return false;
    }
    valueFen += item.amountFen;
    if (!Number.isSafeInteger(valueFen)) return false;
    refundNumbers.add(item.refundNumber);
    previousCreatedAt = createdAt;
    previousRefundNumber = item.refundNumber;
  }
  return valueFen === envelope.valueFen;
}

export function isRefundBusinessMetricDetailSnapshot(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
): value is RefundBusinessMetricDetailSnapshot {
  if (
    !Value.Check(RefundBusinessMetricDetailSnapshotSchema, value) ||
    !envelopeIsConsistent(value) ||
    (expectedScope !== undefined &&
      (value.tenantId !== expectedScope.tenantId ||
        value.storeId !== expectedScope.storeId ||
        value.businessDate !== expectedScope.businessDate))
  ) {
    return false;
  }
  const refundIds = new Set<string>();
  const refundNumberById = new Map<string, RefundBusinessNumber>();
  const paymentFacts = new Map<string, string>();
  for (const item of value.items) {
    let expectedRefundNumber: RefundBusinessNumber;
    try {
      expectedRefundNumber = deriveRefundBusinessNumber(value.businessDate, item.refundId);
    } catch {
      return false;
    }
    if (
      refundIds.has(item.refundId) ||
      item.refundNumber !== expectedRefundNumber ||
      !isOrderNumberForOrderId(item.orderNumber, item.orderId, item.orderBusinessDate)
    ) {
      return false;
    }
    const existingNumber = refundNumberById.get(item.refundId);
    if (existingNumber !== undefined && existingNumber !== item.refundNumber) return false;
    const paymentFact = JSON.stringify([
      item.orderId,
      item.orderNumber,
      item.orderBusinessDate,
      item.paymentMethod,
      item.originalPaymentAmountFen,
    ]);
    const existingPaymentFact = paymentFacts.get(item.paymentId);
    if (existingPaymentFact !== undefined && existingPaymentFact !== paymentFact) return false;
    refundIds.add(item.refundId);
    refundNumberById.set(item.refundId, item.refundNumber);
    paymentFacts.set(item.paymentId, paymentFact);
  }
  return itemsAreConsistent(value.items, value);
}

export function isRefundBusinessMetricDetailReadModel(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
): value is RefundBusinessMetricDetailReadModel {
  return (
    Value.Check(RefundBusinessMetricDetailReadModelSchema, value) &&
    envelopeIsConsistent(value) &&
    (expectedScope === undefined ||
      (value.tenantId === expectedScope.tenantId &&
        value.storeId === expectedScope.storeId &&
        value.businessDate === expectedScope.businessDate)) &&
    value.items.every(
      (item) =>
        item.operatorDisplayName === item.operatorDisplayName.trim() &&
        item.authorizerDisplayName === item.authorizerDisplayName.trim(),
    ) &&
    itemsAreConsistent(value.items, value)
  );
}
